import React, { useState, useEffect } from 'react';
import Image from 'next/image';

interface AppImageProps {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  fill?: boolean;
  priority?: boolean;
  sizes?: string;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  style?: React.CSSProperties;
  fallbackSrc?: string;
  useRegularImg?: boolean;
  autoFallback?: boolean;
  downloadAndHost?: boolean;
}

const AppImage: React.FC<AppImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  fill = false,
  priority = false,
  sizes,
  quality = 75,
  placeholder = 'empty',
  blurDataURL,
  style,
  fallbackSrc = '/assets/images/no_image.png',
  useRegularImg = false,
  autoFallback = true,
  downloadAndHost = false,
  ...props
}) => {
  const [error, setError] = useState(false);
  const [imageLoadAttempts, setImageLoadAttempts] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc);
  const [forceRegularImg, setForceRegularImg] = useState(false);
  const [isLocalImage, setIsLocalImage] = useState(false);

  // Generate intelligent alt text if not provided
  const generateAltText = (imgSrc: string): string => {
    if (alt && alt.trim()) {
      return alt.trim();
    }

    // If no alt provided, generate meaningful alternative text
    if (!imgSrc) {
      return 'Image placeholder';
    }

    // Handle fallback image
    if (imgSrc === fallbackSrc || imgSrc.includes('no_image.png')) {
      return 'Image not available';
    }

    // Extract filename and generate descriptive alt text
    try {
      const filename = imgSrc.split('/').pop()?.split('?')[0] || '';
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      
      // Convert filename to readable description
      const readable = nameWithoutExt
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());

      // Add context based on image source patterns
      if (imgSrc.includes('equipment') || imgSrc.includes('machinery')) {
        return readable ? `${readable} equipment` : 'Construction equipment image';
      } else if (imgSrc.includes('job') || imgSrc.includes('work')) {
        return readable ? `${readable} job posting` : 'Job posting image';
      } else if (imgSrc.includes('news') || imgSrc.includes('article')) {
        return readable ? `${readable} news article` : 'News article image';
      } else if (imgSrc.includes('profile') || imgSrc.includes('avatar')) {
        return readable ? `${readable} profile` : 'User profile image';
      } else if (imgSrc.includes('logo') || imgSrc.includes('brand')) {
        return readable ? `${readable} logo` : 'Company logo';
      } else if (imgSrc.includes('banner') || imgSrc.includes('hero')) {
        return readable ? `${readable} banner` : 'Banner image';
      }

      return readable || 'Image';
    } catch (err) {
      console.warn('Failed to generate alt text from filename:', err);
      return 'Image';
    }
  };

  // Get the appropriate alt text
  const getAltText = (): string => {
    const generatedAlt = generateAltText(error ? fallbackSrc : currentSrc);
    
    // Add error context if image failed to load
    if (error) {
      return `${generatedAlt} (image unavailable)`;
    }
    
    return generatedAlt;
  };

  // Reset state when src changes
  useEffect(() => {
    const validSrc = src || fallbackSrc;
    setError(false);
    setImageLoadAttempts(0);
    setCurrentSrc(validSrc);
    setForceRegularImg(false);
    setIsLocalImage(validSrc?.startsWith('/') || validSrc?.startsWith('./') || false);
  }, [src, fallbackSrc]);

  // Check if the image is from a potentially problematic external domain
  const isExternalRocketImage = (imgSrc: string): boolean => {
    if (!imgSrc || typeof imgSrc !== 'string') return false;
    
    return imgSrc.includes('rocket.new') || 
           imgSrc.includes('img.rocket.new') ||
           imgSrc.includes('cdn.rocket.new') ||
           imgSrc.includes('assets.rocket.new');
  };

  // Check if hostname configuration might be missing
  const isLikelyHostnameError = (imgSrc: string): boolean => {
    if (!imgSrc || typeof imgSrc !== 'string') return false;
    
    // Specific pattern from the error message
    return imgSrc.includes('img.rocket.new/generatedImages/') ||
           (isExternalRocketImage(imgSrc) && imgSrc.includes('rocket_gen_img'));
  };

  // Download and host image locally (simulation - in real app this would be server-side)
  const downloadAndHostImage = async (imageUrl: string): Promise<string> => {
    try {
      // In a real implementation, this would call an API endpoint to download and host the image
      // For now, we'll use a placeholder that represents the locally hosted version
      const filename = imageUrl?.split('/')?.pop() || 'downloaded-image';
      const localPath = `/assets/images/hosted/${filename}`;
      
      console.log(`Image would be downloaded from ${imageUrl} to ${localPath}`);
      
      // For demonstration, return the fallback image path
      // In production, implement actual image downloading service
      return fallbackSrc;
    } catch (downloadError) {
      console.error('Failed to download and host image:', downloadError);
      return fallbackSrc;
    }
  };

  // Handle image loading errors with multiple fallback strategies
  const handleImageError = async () => {
    console.log(`Image load failed for: ${currentSrc}, attempt: ${imageLoadAttempts}`);
    
    if (imageLoadAttempts === 0) {
      // First attempt: try downloading and hosting locally if enabled
      if (downloadAndHost && src && isExternalRocketImage(src)) {
        try {
          const localPath = await downloadAndHostImage(src);
          setImageLoadAttempts(1);
          setCurrentSrc(localPath);
          setIsLocalImage(true);
          return;
        } catch (downloadError) {
          console.error('Download and host failed:', downloadError);
        }
      }
      
      // Fallback to local image
      setImageLoadAttempts(1);
      setCurrentSrc(fallbackSrc);
      setIsLocalImage(true);
    } else if (imageLoadAttempts === 1 && src && isExternalRocketImage(src) && !isLocalImage) {
      // Second attempt: force regular img for external Rocket images
      setImageLoadAttempts(2);
      setForceRegularImg(true);
      setCurrentSrc(src); // Try original src with regular img
    } else {
      // Final attempt failed: use fallback with regular img
      setError(true);
      setForceRegularImg(true);
      setCurrentSrc(fallbackSrc);
      setIsLocalImage(true);
    }
  };

  // Auto-detect problematic images and use appropriate strategy
  const shouldAutoFallback = (): boolean => {
    if (!autoFallback || !src) return false;
    
    // Immediately use regular img for known problematic patterns
    if (isLikelyHostnameError(src)) {
      console.log('Detected likely hostname configuration issue, using regular img');
      return true;
    }
    
    // Auto-fallback for other potentially problematic Rocket images
    if (isExternalRocketImage(src)) {
      return src.includes('generatedImages') || 
             src.includes('rocket_gen_img') ||
             src.includes('/temp/') ||
             src.includes('/cache/');
    }
    
    return false;
  };

  // Check if we should use regular img tag
  const shouldUseRegularImg = (): boolean => {
    // Always use regular img for local images to avoid unnecessary processing
    if (isLocalImage) return false;
    
    // Use regular img if explicitly requested
    if (useRegularImg) return true;
    
    // Force regular img if previous attempts failed
    if (forceRegularImg) return true;
    
    // Auto-fallback for potentially problematic images
    if (shouldAutoFallback()) return true;
    
    // Use regular img if error occurred and it's a Rocket image
    if (error && src && isExternalRocketImage(src)) return true;
    
    // Use regular img if Next.js Image fails for external images
    if (imageLoadAttempts > 0 && isExternalRocketImage(currentSrc) && !isLocalImage) return true;
    
    return false;
  };

  // Get the dynamic alt text for current context
  const dynamicAltText = getAltText();

  // Return fallback immediately if no valid src
  if (!src && !currentSrc) {
    return (
      <img
        src={fallbackSrc}
        alt={dynamicAltText}
        width={width}
        height={height}
        className={`${className} opacity-75`}
        style={style}
        loading={priority ? 'eager' : 'lazy'}
        {...props}
      />
    );
  }

  // Enhanced regular img tag with better error handling
  if (shouldUseRegularImg()) {
    return (
      <img
        src={error ? fallbackSrc : currentSrc}
        alt={dynamicAltText}
        width={width}
        height={height}
        className={`${className} ${error ? 'opacity-75' : ''}`}
        style={style}
        onError={handleImageError}
        loading={priority ? 'eager' : 'lazy'}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        decoding="async"
        {...props}
      />
    );
  }

  // Common props for Next.js Image component
  const commonProps = {
    src: currentSrc,
    alt: dynamicAltText,
    className: error ? `${className} opacity-75` : className,
    priority,
    quality,
    placeholder,
    blurDataURL,
    style,
    onError: handleImageError,
    // Only unoptimized for problematic external images
    unoptimized: isExternalRocketImage(currentSrc) && !isLocalImage,
    ...props,
  };

  try {
    if (fill) {
      return (
        <Image
          {...commonProps}
          fill
          sizes={sizes || '100vw'}
        />
      );
    }

    return (
      <Image
        {...commonProps}
        width={width || 500}
        height={height || 300}
        sizes={sizes}
      />
    );
  } catch (nextImageError) {
    console.log('Next.js Image component failed, falling back to regular img:', nextImageError);
    
    // Force regular img on next render
    if (!forceRegularImg) {
      setForceRegularImg(true);
      return null; // Trigger re-render
    }
    
    return (
      <img
        src={error ? fallbackSrc : currentSrc}
        alt={dynamicAltText}
        width={width}
        height={height}
        className={`${className} ${error ? 'opacity-75' : ''}`}
        style={style}
        onError={handleImageError}
        loading={priority ? 'eager' : 'lazy'}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        decoding="async"
        {...props}
      />
    );
  }
};

export default AppImage;